import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Trophy, Shield } from "lucide-react";

// One-way "Complete Raid" trigger for the Master GM — closes the raid out and credits every
// raid_member's Gauntlet points (tournament/dungeon crawl score themselves automatically from
// already-tracked data; Alliance/BBEBBEG/Party have no auto-tracked score, so the Master's own
// optional bonus points here are the only way those earn more than the flat participation
// award — see gauntlet_engine.py). Shows the awarded breakdown once done.
export function CompleteRaidPanel({ raid, raidId, isMasterGm, api, formatApiErrorDetail, onCompleted }) {
  const [bonusDrafts, setBonusDrafts] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!raid) return null;
  const members = raid.members || [];

  if (raid.status === "completed") {
    const awards = raid.gauntlet_awards || {};
    return (
      <div className="panel-wood rounded-2xl p-5 space-y-3 lg:col-span-2" data-testid="raid-complete-panel">
        <h2 className="font-display text-xl flex items-center gap-2"><Trophy className="w-4 h-4 text-[#D4AF37]" /> Raid Completed — Gauntlet Points Awarded</h2>
        <div className="space-y-1.5">
          {members.map((m) => {
            const entry = awards[m.user_id];
            if (!entry) return null;
            return (
              <div key={m.id} className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2" data-testid={`raid-gauntlet-award-${m.id}`}>
                <span className="text-sm">{m.display_name}</span>
                <span className="text-xs text-[#f1e0c5]/50">{(entry.reasons || []).join(", ")} <strong className="text-[#D4AF37]">= {entry.gauntlet_points}</strong></span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (!isMasterGm) {
    return (
      <div className="panel-wood rounded-2xl p-5 space-y-1 lg:col-span-2" data-testid="raid-complete-panel">
        <h2 className="font-display text-xl flex items-center gap-2"><Shield className="w-4 h-4 text-[#f1e0c5]/40" /> Gauntlets</h2>
        <p className="text-xs text-[#f1e0c5]/40">The Master GM completes this raid once it's done to award everyone's Gauntlet points.</p>
      </div>
    );
  }

  const completeRaid = async () => {
    setBusy(true);
    try {
      const bonus_points = Object.fromEntries(Object.entries(bonusDrafts).filter(([, v]) => parseInt(v, 10) > 0).map(([k, v]) => [k, parseInt(v, 10)]));
      const { data } = await api.post(`/raids/${raidId}/complete`, { bonus_points });
      onCompleted({ status: "completed", gauntlet_awards: data.awards });
      toast.success("Raid completed — Gauntlet points awarded!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <div className="panel-wood rounded-2xl p-5 space-y-3 lg:col-span-2" data-testid="raid-complete-panel">
      <h2 className="font-display text-xl flex items-center gap-2"><Trophy className="w-4 h-4 text-[#D4AF37]" /> Gauntlets</h2>
      {!confirming ? (
        <Button data-testid="raid-complete-open-button" variant="secondary" className="rounded-full gap-1.5" onClick={() => setConfirming(true)}>
          <Trophy className="w-3.5 h-3.5" /> Complete Raid &amp; Award Gauntlet Points
        </Button>
      ) : (
        <div className="space-y-2">
          <p className="text-[10px] text-[#f1e0c5]/40">
            Everyone gets a flat participation award; Tournament/Dungeon Crawl also add their own tracked scores automatically. Add optional bonus points below for anything else worth rewarding (MVP performance, roleplay, etc.) — this can't be undone.
          </p>
          <div className="space-y-1.5">
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-2 bg-black/20 rounded-lg px-3 py-1.5" data-testid={`raid-complete-bonus-row-${m.id}`}>
                <span className="text-sm">{m.display_name} <span className="text-[10px] uppercase text-[#f1e0c5]/30">{m.role === "master_gm" ? "Master GM" : "General"}</span></span>
                <Input
                  data-testid={`raid-complete-bonus-input-${m.id}`}
                  type="number"
                  min={0}
                  placeholder="Bonus pts"
                  value={bonusDrafts[m.id] || ""}
                  onChange={(e) => setBonusDrafts((d) => ({ ...d, [m.id]: e.target.value }))}
                  className="bg-black/30 border-white/10 h-8 w-24 text-xs"
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button data-testid="raid-complete-confirm-button" disabled={busy} onClick={completeRaid} className="rounded-full bg-[#D90429] hover:bg-[#b8031f] gap-1.5">
              {busy ? "Awarding…" : "Confirm & Complete Raid"}
            </Button>
            <Button data-testid="raid-complete-cancel-button" variant="ghost" disabled={busy} onClick={() => setConfirming(false)} className="rounded-full">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
